"use client";

import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Typography,
} from "@mui/material";
import React, { FC, useState } from "react";
import { Button } from "../../Button";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { isEmpty, isNil } from "lodash";
import { DesktopProposalField } from "./DesktopProposalField";
import { strings } from "@/src/shared/strings";
import { getColumnCell, getFieldName } from "../lib/utils";
import { PdfLinkCell } from "./PdfLinkCell";
import { DesktopColumnHeaderContent } from "./DesktopColumnHeaderContent";
import { useProposalTableContext } from "./ProposalTableProvider";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "next/navigation";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { logTravelAction } from "@/src/api/server-actions/analytics";

export const DesktopProposalTable: FC = () => {
  const {
    proposalData,
    rows,
    columns,
    pdfFile,
    selectedColumnIndex,
    editingColumnIndex,
    onCancel,
    onSelectColumnCard,
    onRecalculate,
    onChangeCover,
    onPdfFileClick,
    isTariffCalcPending,
  } = useProposalTableContext();

  const { getValues } = useFormContext();
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);
  const [isChangeCoverPending, setIsChangeCoverPending] = useState(false);

  const logTableAction = (actionName: string, packageName: string | null | undefined) => {
    void logTravelAction({
      actionName: `proposalTable_${actionName}`,
      processInstanceId: processInstanceId ?? "unknown",
      url: typeof window !== "undefined" ? window.location.href : "",
      context: { packageName: packageName ?? null },
    });
  };

  return (
    <TableContainer className="rounded-lg px-0 shadow-proposal-table max-mdPlus:hidden">
      <Table
        aria-label="proposal table"
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <TableHead>
          <TableRow className="border-0 border-b border-dashed border-bulish-gray">
            <TableCell className="h-[166px]">
              {!pdfFile?.hidden && (
                <PdfLinkCell
                  iconName={pdfFile?.icon?.name}
                  iconText={pdfFile?.title}
                  url={pdfFile?.srcUrl}
                  onClick={onPdfFileClick}
                />
              )}
            </TableCell>
            {columns?.map((column, colIndex) => {
              const isSelected = colIndex === selectedColumnIndex;
              const isEditing = colIndex === editingColumnIndex;

              return (
                <TableCell
                  align="center"
                  key={column?.title ?? colIndex}
                  className={cn("relative p-0", {
                    "opacity-60": !isNil(editingColumnIndex) && !isEditing,
                  })}
                >
                  <DesktopColumnHeaderContent
                    title={column?.title}
                    icon={column?.icon}
                    price={column?.price}
                    isSelected={isSelected}
                    isEditing={isEditing}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              className={cn({
                "border-0 border-b border-dashed border-bulish-gray":
                  rowIndex === rows.length - 1,
              })}
            >
              <TableCell
                component="th"
                scope="row"
                className={cn({
                  "bg-super-light-blue": rowIndex % 2 !== 0,
                })}
              >
                {row?.title}
              </TableCell>
              {columns?.map((column, colIndex) => {
                const isSelected = colIndex === selectedColumnIndex;
                const isEditing = colIndex === editingColumnIndex;
                const columnCell = getColumnCell({
                  proposalData,
                  rowKey: row?.key,
                  columnIndex: colIndex,
                  columnId: column?.title,
                  daysAbroad: getValues("daysAbroad"),
                });

                const renderFieldItem =
                  columnCell && !isEmpty(columnCell?.fields);

                return (
                  <TableCell
                    key={colIndex}
                    align="center"
                    className={cn("relative p-0", {
                      // "shadow-active-proposal-column": isSelected || isEditing,
                      // "bg-[#f8f9fa] text-[#c2c2c2] shadow-inactive-proposal-column inactive-proposal-cell":
                      //   isInactive,
                      "opacity-60": !isNil(editingColumnIndex) && !isEditing,
                    })}
                  >
                    <>
                      {!!(isSelected || isEditing) && (
                        <div className="absolute inset-0 z-30 shadow-active-proposal-cell"></div>
                      )}
                      <Stack
                        className={cn(
                          "absolute inset-0 justify-center gap-1 bg-white p-3 flex-center-row",
                          {
                            "bg-super-light-blue": rowIndex % 2 !== 0,
                            "z-40": isSelected || isEditing,
                          },
                        )}
                        sx={{
                          zIndex:
                            isSelected || isEditing
                              ? 100 - rowIndex + "!important"
                              : 0,
                        }}
                      >
                        {renderFieldItem ? (
                          columnCell.fields.map((fieldItem) => {
                            const name = getFieldName({
                              columnId: column.title,
                              fieldName: fieldItem.name,
                            });
                            return (
                              <DesktopProposalField
                                key={name}
                                name={name}
                                columnId={column.title}
                                isEditing={isEditing}
                                fieldItem={fieldItem}
                                editable={!row.editable}
                              />
                            );
                          })
                        ) : (
                          <Typography>{strings.common.noData}</Typography>
                        )}
                      </Stack>
                    </>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          <TableRow>
            <TableCell />
            {columns?.map((column, columnIndex) => {
              const isSelected = columnIndex === selectedColumnIndex;
              const isEditing = columnIndex === editingColumnIndex;

              const handleChose = async () => {
                logTableAction("choose", column.title);
                setIsChangeCoverPending(true);
                await onSelectColumnCard(columnIndex, column.title);
                setIsChangeCoverPending(false);
              };

              return (
                <TableCell
                  align="center"
                  key={columnIndex}
                  className={cn("relative p-0", {
                    "opacity-60": !isNil(editingColumnIndex) && !isEditing,
                  })}
                >
                  {!!(isSelected || isEditing) && (
                    <div
                      className={cn(
                        "absolute inset-0 z-10 shadow-active-proposal-cell",
                        {
                          "rounded-b-lg": isSelected || isEditing,
                        },
                      )}
                    ></div>
                  )}
                  <Stack
                    direction="column"
                    className={cn("relative bg-white px-4 pb-3 pt-6", {
                      "rounded-b-lg": isSelected || isEditing,
                      "z-40": isSelected || isEditing,
                    })}
                    spacing={1}
                  >
                    {isEditing ? (
                      <>
                        <Button
                          variant="contained"
                          onClick={() => {
                            if (!isNil(column.title)) {
                              logTableAction("recalculate", column.title);
                              onRecalculate(columnIndex, column.title);
                            }
                          }}
                          loading={isTariffCalcPending}
                          className="whitespace-normal break-words"
                        >
                          {strings.proposalTable.recalculate}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            logTableAction("cancel", column.title);
                            onCancel(columnIndex, column.title);
                          }}
                          disabled={isTariffCalcPending}
                          className="whitespace-normal break-words"
                        >
                          {strings.common.cancel}
                        </Button>
                      </>
                    ) : isSelected ? (
                      <>
                        <Button
                          variant="contained"
                          className="whitespace-normal break-words"
                          startIcon={
                            <WebShopIcon
                              name="check"
                              width={16}
                              height={16}
                              fill={"var(--white)"}
                            />
                          }
                          loading={isChangeCoverPending}
                          onClick={handleChose}
                        >
                          {strings.common.chose}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            logTableAction("changeCover", column.title);
                            onChangeCover(columnIndex, column.title);
                          }}
                          disabled={isTariffCalcPending}
                          className="whitespace-normal break-words"
                        >
                          {strings.proposalTable.changeCover}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          onClick={handleChose}
                          disabled={isTariffCalcPending}
                          className="whitespace-normal break-words"
                        >
                          {strings.common.choose}
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={isTariffCalcPending}
                          onClick={() => {
                            logTableAction("changeCover", column.title);
                            onChangeCover(columnIndex, column.title);
                          }}
                          className="whitespace-normal break-words"
                        >
                          {strings.proposalTable.changeCover}
                        </Button>
                      </>
                    )}
                  </Stack>
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
